import React, { FC, useEffect, useState, useCallback, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import {
  Box,
  SkeletonGroup,
  SkeletonRectangle,
  SkeletonLine,
} from '@wix/design-system';
import { TemplateGalleryCard } from '@common/types';
import { useIntersectionObserver } from 'usehooks-ts';
import { templatePath } from '@components/Hooks/useRouter';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import { usePriceCalculation } from '@components/Hooks/usePriceCalculation';
import CardInfo from '../CardInfo';
import CardCarousel from '../CardCarousel/CardCarousel';
import type { ActionButtonVisibility } from '../TemplateCard';
import s from '../TemplateCard.module.scss';

export interface TemplateCardMobileProps {
  template: TemplateGalleryCard;
  forceRender?: boolean;
  loader?: boolean;
  showShadow?: boolean;
  onCustomClick?: () => void;
  actionButton?: React.ReactNode;
  actionButtonVisibility?: ActionButtonVisibility;
  cardActions?: React.ReactElement;
  entryParam?: string;
}

const TemplateCardMobile: FC<TemplateCardMobileProps> = ({
  template,
  forceRender,
  loader = false,
  showShadow = true,
  onCustomClick,
  actionButton,
  actionButtonVisibility = 'always',
  cardActions,
  entryParam,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);
  const [ready, setReady] = useState(false || forceRender);
  const { isIntersecting, ref } = useIntersectionObserver({});
  const { logBiClick } = useTemplatesBi({
    is_mobile: true,
  });
  const { getLocalizedTotalPrice } = usePriceCalculation();

  useEffect(() => {
    if (isIntersecting && !ready && !loader) {
      setTimeout(() => {
        setReady(true);
      }, 200);
    }
  }, [ready, isIntersecting]);

  function buildTemplatePageLink() {
    if (!template.id) return undefined;
    const base = `${templatePath}/${template.id}`;
    return entryParam ? `${base}?entry=${entryParam}` : base;
  }

  function renderTemplateCard() {
    return (
      <Box
        className={`${s.template} ${s.mobileView}`}
        direction="vertical"
        minWidth="243px"
        minHeight="260.88px"
        overflow="hidden"
        dataHook="mobile-card"
      >
        <CardCarousel
          setFocused={template.media?.animatedPreview ? setFocused : undefined}
          imageSources={template.media?.additionalImages}
          animatedPreview={template.media?.animatedPreview}
          focused={focused}
          hover={hover}
        />
        <CardInfo
          template={template}
          isMobileView={true}
          titleProps={{ weight: 'normal', size: 'medium' }}
          priceTextProps={{
            weight: 'bold',
            getLocalizedPrice: getLocalizedTotalPrice,
          }}
          t={t}
          language={language}
        />
      </Box>
    );
  }

  function renderLoadingCard() {
    return (
      <SkeletonGroup skin="light">
        <Box
          className={`${s.template} ${
            showShadow ? s.cardShadow : s.cardBorder
          }`}
          direction="vertical"
          minWidth="243px"
          minHeight="260.88px"
          padding="12px"
          overflow="hidden"
          borderRadius="4px"
        >
          <div ref={ref}></div>
          <SkeletonRectangle height="203px" />
          <Box direction="horizontal" width="100%" align="space-between">
            <SkeletonLine width="222px" marginTop="10px" />
            <SkeletonLine width="64" marginTop="10px" />
          </Box>
          <SkeletonLine width="222px" marginTop="6px" />
          <SkeletonLine width="84px" marginTop="32px" />
        </Box>
      </SkeletonGroup>
    );
  }

  const handleClick = useCallback(() => {
    if (onCustomClick) {
      onCustomClick();
    } else {
      logBiClick({
        click_type: 'Use template',
        click_data: { template_id: template.id },
      });
    }
  }, [onCustomClick, logBiClick, template.id]);

  const templateHref = useMemo(() => buildTemplatePageLink(), [template.id]);
  const renderCardActions = () =>
    cardActions ? (
      <div className={s.mobileCardActions}>{cardActions}</div>
    ) : null;

  if (templateHref) {
    return (
      <div
        aria-labelledby={`template-title-${template.title}`}
        className={s.cardWrapper}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => {
          setHover(false);
          template.media?.animatedPreview && setFocused(false);
        }}
      >
        <a
          data-hook={`template-card-mobile-container-${template.id}`}
          href={templateHref}
          aria-label={t('marketplace.templates-page.view-template-cta')}
          className={s.cardContainer}
          onClick={handleClick}
        >
          {ready ? renderTemplateCard() : renderLoadingCard()}
        </a>
        {ready && renderCardActions()}
        {actionButton && (
          <div
            className={`${s.templateCardActionButton} ${
              actionButtonVisibility === 'hover'
                ? s.templateCardActionButtonHover
                : ''
            }`}
          >
            {actionButton}
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div
        aria-labelledby={`template-title-${template.title}`}
        className={s.cardWrapper}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => {
          setHover(false);
          template.media?.animatedPreview && setFocused(false);
        }}
      >
        <div
          data-hook={`template-card-mobile-container-${template.id}`}
          aria-label={t('marketplace.templates-page.view-template-cta')}
          className={s.cardContainer}
        >
          {ready ? renderTemplateCard() : renderLoadingCard()}
        </div>
        {ready && renderCardActions()}
        {actionButton && (
          <div
            className={`${s.templateCardActionButton} ${
              actionButtonVisibility === 'hover'
                ? s.templateCardActionButtonHover
                : ''
            }`}
          >
            {actionButton}
          </div>
        )}
      </div>
    );
  }
};

export default TemplateCardMobile;
